import React, { useEffect, useState } from "react";
import "./ContactBanner.css";

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  note: string;
};

type FieldErrors = Partial<Record<keyof FormState, string>>;

const initialState: FormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  companyName: "",
  note: "",
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern = /^[0-9+\s()-]{6,}$/;

const isLocalHost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

const CONTACT_ENDPOINT = isLocalHost
  ? "/api/contact"
  : process.env.REACT_APP_CONTACT_ENDPOINT?.trim() || "/api/contact.php";

const contactHighlights = [
  "Fast response from our team",
  "Custom quote based on your business needs",
  "Flexible date and time for a callback",
];

const validateForm = (form: FormState): FieldErrors => {
  const errors: FieldErrors = {};

  if (!form.firstName.trim()) {
    errors.firstName = "Please enter your first name.";
  }

  if (!form.lastName.trim()) {
    errors.lastName = "Please enter your last name.";
  }

  if (!form.email.trim()) {
    errors.email = "Please enter your email.";
  } else if (!emailPattern.test(form.email.trim())) {
    errors.email = "Please enter a valid email address.";
  }

  if (!form.phone.trim()) {
    errors.phone = "Please enter your phone number.";
  } else if (!phonePattern.test(form.phone.trim())) {
    errors.phone = "Please enter a valid phone number.";
  }

  if (!form.companyName.trim()) {
    errors.companyName = "Please enter your company name.";
  }

  if (!form.note.trim()) {
    errors.note = "Please enter a short note.";
  }

  return errors;
};

const ContactBanner: React.FC = () => {
  const [form, setForm] = useState<FormState>(initialState);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || "info@succit.gr";

  useEffect(() => {
    if (!errorMsg && !successMsg) return;

    const timeout = setTimeout(() => {
      setErrorMsg(null);
      setSuccessMsg(null);
    }, 6000);

    return () => clearTimeout(timeout);
  }, [errorMsg, successMsg]);

  const onChange =
    (key: keyof FormState) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;

        setForm((prev) => ({ ...prev, [key]: value }));
        setErrorMsg(null);
        setSuccessMsg(null);

        setFieldErrors((prev) => {
          if (!prev[key]) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setSuccessMsg(null);
    setErrorMsg(null);

    const errors = validateForm(form);
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setSending(true);

    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        companyName: form.companyName.trim(),
        note: form.note.trim(),
      };

      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = (await response.json().catch(() => null)) as { message?: string } | null;

      if (!response.ok) {
        throw new Error(data?.message || `Request failed with status ${response.status}.`);
      }

      setForm(initialState);
      setFieldErrors({});
      setSubmitted(false);
      setSuccessMsg(
        data?.message ||
        "Your request was sent successfully. We will contact you as soon as possible."
      );
    } catch (err: unknown) {
      console.error("Contact form error", err);

      let message = "Something went wrong while sending your request.";

      if (err instanceof Error && err.message) {
        message = err.message;
      }

      setErrorMsg(`${message} Please try again or contact us directly.`);
      setSubmitted(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="contact-banner">
      <div className="contact-banner-overlay"></div>

      <div className="contact-banner__container">
        <div className="contact-banner__intro">
          {/* <p className="contact-banner__eyebrow">Get In Touch</p> */}
          <h2 className="contact-banner__title">Planning a project, event, or brand activation?</h2>
          <p className="contact-banner__desc">
            Share your ideas and needs with us and our team will provide a
            tailored proposal to bring your project to life!
          </p>
          <div className="contact-banner__meta">
            <div className="contact-banner__meta-item">
              <span className="contact-banner__meta-label">Email</span>
              <a href={`mailto:info@succit.gr`} className="contact-banner__meta-value">
                info@succit.gr
              </a>

              <span className="contact-banner__meta-label">Location</span>
              <a href={`https://maps.app.goo.gl/Qe8m6grmBtYvLz5dA`} className="contact-banner__meta-value">
                Office: Leoforos Alexandras 209 (by appointment only), Athens, 11523 – Greece
              </a>
            </div>
          </div>
        </div>

        <form className="contact-banner__form" onSubmit={handleSubmit} noValidate>
          <div className="contact-banner__fields">
            <label className="contact-banner__field">
              <span className="contact-banner__label">First Name*</span>
              <input
                className={`contact-banner__input ${submitted && fieldErrors.firstName ? "is-invalid" : ""
                  }`}
                type="text"
                name="firstName"
                value={form.firstName}
                onChange={onChange("firstName")}
                autoComplete="given-name"
                required
              />
              {submitted && fieldErrors.firstName && (
                <span className="contact-banner__field-error">{fieldErrors.firstName}</span>
              )}
            </label>

            <label className="contact-banner__field">
              <span className="contact-banner__label">Last Name*</span>
              <input
                className={`contact-banner__input ${submitted && fieldErrors.lastName ? "is-invalid" : ""
                  }`}
                type="text"
                name="lastName"
                value={form.lastName}
                onChange={onChange("lastName")}
                autoComplete="family-name"
                required
              />
              {submitted && fieldErrors.lastName && (
                <span className="contact-banner__field-error">{fieldErrors.lastName}</span>
              )}
            </label>

            <label className="contact-banner__field">
              <span className="contact-banner__label">Email*</span>
              <input
                className={`contact-banner__input ${submitted && fieldErrors.email ? "is-invalid" : ""
                  }`}
                type="email"
                name="email"
                value={form.email}
                onChange={onChange("email")}
                autoComplete="email"
                required
              />
              {submitted && fieldErrors.email && (
                <span className="contact-banner__field-error">{fieldErrors.email}</span>
              )}
            </label>

            <label className="contact-banner__field">
              <span className="contact-banner__label">Phone*</span>
              <input
                className={`contact-banner__input ${submitted && fieldErrors.phone ? "is-invalid" : ""
                  }`}
                type="tel"
                name="phone"
                value={form.phone}
                onChange={onChange("phone")}
                autoComplete="tel"
                inputMode="tel"
                pattern="^[0-9+\\s()-]{6,}$"
                required
              />
              {submitted && fieldErrors.phone && (
                <span className="contact-banner__field-error">{fieldErrors.phone}</span>
              )}
            </label>

            <label className="contact-banner__field">
              <span className="contact-banner__label">Company Name</span>
              <input
                className={`contact-banner__input ${submitted && fieldErrors.companyName ? "is-invalid" : ""
                  }`}
                type="text"
                name="companyName"
                value={form.companyName}
                onChange={onChange("companyName")}
                autoComplete="organization"
              />
              {submitted && fieldErrors.companyName && (
                <span className="contact-banner__field-error">{fieldErrors.companyName}</span>
              )}
            </label>


            <label className="contact-banner__field">
              <span className="contact-banner__label">Message*</span>
              <textarea
                className={`contact-banner__input contact-banner__textarea ${submitted && fieldErrors.note ? "is-invalid" : ""
                  }`}
                name="note"
                value={form.note}
                onChange={onChange("note")}
                placeholder="Write a few details about what you need, quantities, timeline, or any specific request..."
                rows={5}
                required
              />
              {submitted && fieldErrors.note && (
                <span className="contact-banner__field-error">{fieldErrors.note}</span>
              )}
            </label>
          </div>

          <div className="contact-banner__actions">
            <p className="contact-banner__note">
              Fields marked with * are required. We use these details only to contact
              you back regarding your request.
            </p>

            <button className="contact-banner__submit" type="submit" disabled={sending}>
              {sending ? "Sending..." : "Send Email"}
            </button>

            {errorMsg && (
              <div className="contact-banner__feedback contact-banner__feedback--error" role="alert">
                {errorMsg}
              </div>
            )}

            {successMsg && (
              <div className="contact-banner__feedback contact-banner__feedback--success" role="status">
                {successMsg}
              </div>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactBanner;
